import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface UseLeaveConfirmationOptions {
  shouldConfirm: boolean;
  message?: string;
}

interface UseLeaveConfirmationResult {
  showConfirmation: () => void;
  cancelNavigation: () => void;
  confirmNavigation: () => void;
  navigateWithoutConfirmation: (path: string) => void;
  showingConfirmation: boolean;
  pendingPath: string | null;
}

export const useLeaveConfirmation = ({
  shouldConfirm,
  message = 'You have unsaved changes. Are you sure you want to leave this page?'
}: UseLeaveConfirmationOptions): UseLeaveConfirmationResult => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [showingConfirmation, setShowingConfirmation] = useState(false);
  
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!shouldConfirm) return;
      
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldConfirm, message]);
  
  const showConfirmation = useCallback(() => {
    if (shouldConfirm) {
      setShowingConfirmation(true);
    } else {
      navigate('/admin/contracts');
    }
  }, [shouldConfirm, navigate]);
  
  const cancelNavigation = useCallback(() => {
    setShowingConfirmation(false);
    setPendingPath(null);
  }, []);
  
  const confirmNavigation = useCallback(() => {
    setShowingConfirmation(false);
    
    if (pendingPath) {
      navigate(pendingPath);
      setPendingPath(null);
    } else {
      navigate('/admin/contracts');
    }
  }, [navigate, pendingPath]);
  
  const navigateWithoutConfirmation = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);
  
  return {
    showConfirmation,
    cancelNavigation,
    confirmNavigation,
    navigateWithoutConfirmation,
    showingConfirmation,
    pendingPath,
  };
}; 
